import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import supabase from '../lib/supabase';

type Row = {
  id: string;
  user_id: string;
  status: string;
  ai_confidence: number | null;
  ai_reasoning: string | null;
  extracted_name: string | null;
  extracted_eid: string | null;
  created_at: string;
};

export default function Verifications() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('identity_verifications')
      .select('id, user_id, status, ai_confidence, ai_reasoning, extracted_name, extracted_eid, created_at')
      .eq('status', 'pending_review')
      .order('created_at', { ascending: false });
    setRows((data as Row[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const act = async (id: string, action: 'approve' | 'reject') => {
    setActing(id);
    await supabase.functions.invoke('review-identity-verification', {
      body: { verificationId: id, action },
    });
    setActing(null);
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <div className="p-8">
      <h1 className="text-2xl mb-1">Identity verifications</h1>
      <p className="text-sm text-gray-500 mb-6">AI-flagged submissions awaiting review.</p>

      <div className="space-y-3">
        {rows.length === 0 ? (
          <div className="text-center text-gray-500 py-10 bg-white rounded-xl border border-gray-200">
            {loading ? 'Loading…' : 'Nothing waiting for review.'}
          </div>
        ) : (
          rows.map((r) => (
            <div key={r.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">{r.extracted_name || '(no name extracted)'}</div>
                  <div className="text-xs text-gray-500">EID: {r.extracted_eid || '—'}</div>
                  <div className="text-xs text-gray-500">
                    Confidence: {r.ai_confidence == null ? '—' : (r.ai_confidence * 100).toFixed(0) + '%'}
                  </div>
                  {r.ai_reasoning && (
                    <p className="text-xs text-gray-600 mt-2 max-w-2xl whitespace-pre-wrap">{r.ai_reasoning}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => act(r.id, 'reject')}
                    disabled={acting === r.id}
                    className="h-9 px-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm disabled:opacity-50"
                  >
                    {acting === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reject'}
                  </button>
                  <button
                    onClick={() => act(r.id, 'approve')}
                    disabled={acting === r.id}
                    className="h-9 px-3 rounded-lg bg-[#2d5016] hover:bg-[#1a3010] text-white text-sm disabled:opacity-50"
                  >
                    {acting === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Approve'}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
